import { TRPCLink } from "@trpc/client";
import { AnyRouter } from "@trpc/server";
import { observable, Unsubscribable } from "@trpc/server/observable";

/**
 * WebSocket-hostile transports, and the fallback that survives them
 * (#1230 point 5).
 *
 * Everything about the conversation used to ride one tRPC WebSocket: the
 * `app.ws.*` namespace carries the live AG-UI event subscription *and* the
 * mutations that drive a run (send message, stop, undo, redo, clone). The
 * initial snapshot arrives as the subscription's first `STATE_SNAPSHOT` — the
 * page deliberately renders from an empty snapshot until then — so on a
 * network that cannot open a WebSocket the chat panel sits on
 * "Loading conversation..." forever. That is not hypothetical: sandboxed agent
 * sessions (our own preview verification harness included) and locked-down
 * corporate/mobile proxies hit it, and tRPC's WS client makes it worse by
 * silently retrying forever — a subscription that never connects never errors,
 * so nothing downstream can react.
 *
 * The fix keeps WebSocket primary and adds a fallback for exactly the
 * connections that cannot use it:
 *
 * - `createWsTransportHealth()` watches the WS client's connection callbacks
 *   and decides, once per page session, that WebSocket is unusable here.
 * - `subscriptionWithFallbackLink()` starts a subscription over WS and swaps it
 *   for the SSE link (`unstable_httpSubscriptionLink`, plain HTTP
 *   `text/event-stream`) when WS is judged unusable — or when the WS attempt
 *   produces no event at all within {@link SUBSCRIPTION_FIRST_EVENT_TIMEOUT_MS},
 *   which covers the proxy that completes the handshake and then swallows
 *   frames.
 * - `preferHttpWhenWsBrokenLink()` sends the namespace's queries/mutations over
 *   HTTP once WS is known broken. It decides at operation start and never
 *   mid-flight: a mutation that may already have reached the server must not be
 *   replayed on another transport.
 *
 * Design notes:
 * - The fallback is one-way and sticky for the page's lifetime. Flapping
 *   between transports would re-subscribe (and re-snapshot) on every blip for
 *   no user-visible gain; SSE carries the identical event vocabulary, so
 *   staying there costs nothing but a held HTTP request.
 * - The server needs no new endpoint: the subscription is the same tRPC
 *   procedure, and its resolver already returns an async generator (which is
 *   what tRPC's SSE producer consumes). Reconnects need no `Last-Event-Id`
 *   resume either — every (re)subscribe replays a full `STATE_SNAPSHOT`, and
 *   the AG-UI reducer treats that as a replace.
 */

/**
 * Absolute `ws(s)://` URL for a same-origin path — what `wsLink`'s `url` must
 * be, because tRPC hands it straight to `new WebSocket()`.
 *
 * A relative URL there is a **Safari ≤16 bug of ours** (#1213, Sentry
 * DASHBOARD-WORKER-EQ/EN): WebKit only gained the WebSocket constructor's
 * relative-URL resolution — and the `http:`→`ws:` / `https:`→`wss:` upgrade the
 * URL spec asks for — in Safari 17, so on Safari 16 and older every
 * `new WebSocket("/trpc/ws")` throws
 * `SyntaxError: The string did not match the expected pattern.` (WebKit's
 * generic `DOMException` text) while Chrome, Firefox and Safari 17+ resolve it
 * happily. Verified against real editor pages in WebKit 16.4 vs 17.0 vs 26
 * (`scripts/preview/safari-rejection-probe.mjs`).
 *
 * The Sentry noise was the visible half. The user-visible half: with no socket
 * ever opening, an affected browser only loads its conversation once
 * {@link SUBSCRIPTION_FIRST_EVENT_TIMEOUT_MS} hands the subscription to SSE —
 * ten seconds of "Loading conversation..." on every editor page — and a
 * `ws`-namespace mutation issued before the health signal flips
 * ({@link WS_CONNECT_FAILURES_BEFORE_FALLBACK}) is buffered on a connection
 * that will never exist, so an early send/stop/undo is simply lost.
 *
 * Resolving the URL ourselves is browser-agnostic: every engine accepts an
 * absolute `wss://` URL.
 *
 * `base` is a served page's `window.location.href`, so it is always `http:` or
 * `https:`; the mapping is spelled out both ways rather than defaulting, so a
 * caller that ever passes something else (`file:`, `blob:`) fails loudly
 * instead of silently claiming `wss:`.
 */
export const websocketUrl = (path: string, base: string): string => {
  const url = new URL(path, base);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`websocketUrl: expected an http(s) base, got ${url.protocol}`);
  }
  url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
  return url.toString();
};

/**
 * Consecutive failed WS connection attempts before falling back. tRPC's WS
 * client reports every failed attempt through `onError` (including a throwing
 * `new WebSocket()`) and resets the count on a successful open, so the counter
 * means "we could not get a socket, and retrying did not help".
 *
 * Three, not two, because tRPC's first retry is immediate
 * (`exponentialBackoff(0) === 0`): a momentary outage on an otherwise
 * WS-capable network produces two failures within milliseconds of each other,
 * and the third attempt only comes ~2s later — by which time a blip has
 * healed and `onOpen` has reset the count. So the third strike is what
 * separates "the network cannot do WebSocket" from "the network hiccuped".
 *
 * Deliberately *not* gated on "no connection ever opened in this page
 * session", which was considered: it would spare the blip case at the cost of
 * never falling back for a client whose network turns WS-hostile mid-session
 * (a phone handing off to a captive-portal proxy), i.e. it trades a harmless
 * outcome — SSE carries the identical events — for a silent freeze. The
 * unnecessary fallback is the cheap mistake here; missing a needed one is the
 * bug this whole module exists to fix.
 */
export const WS_CONNECT_FAILURES_BEFORE_FALLBACK = 3;

/**
 * How long a WS subscription may produce nothing before we fall back anyway.
 * The health signal above is the fast path; this is the backstop for a
 * transport that looks connected but delivers nothing (handshake-completing
 * proxies, a wedged session). Generous on purpose — every event that does
 * arrive cancels it, so it only ever fires on a stream that is already dead.
 */
export const SUBSCRIPTION_FIRST_EVENT_TIMEOUT_MS = 10_000;

export type WsTransportHealth = {
  /** True once WebSocket has been judged unusable for this page session. */
  readonly hasFallenBack: () => boolean;
  /** Declare WebSocket unusable (idempotent). */
  readonly fallBack: (reason: string) => void;
  /** Notified when — and only when — `hasFallenBack()` flips to true. */
  readonly onFallBack: (listener: () => void) => () => void;
  /** `createWSClient({ ...health.wsClientCallbacks })`. */
  readonly wsClientCallbacks: {
    readonly onOpen: () => void;
    readonly onError: (event?: Event) => void;
  };
};

export const createWsTransportHealth = ({
  onFallBack,
  failuresBeforeFallback = WS_CONNECT_FAILURES_BEFORE_FALLBACK,
}: {
  /** Side-channel for logging/telemetry; called once, with the deciding reason. */
  onFallBack?: (reason: string) => void;
  failuresBeforeFallback?: number;
} = {}): WsTransportHealth => {
  let fallenBack = false;
  let consecutiveFailures = 0;
  const listeners = new Set<() => void>();

  const fallBack = (reason: string) => {
    if (fallenBack) {
      return;
    }
    fallenBack = true;
    onFallBack?.(reason);
    // Snapshot: a listener may unsubscribe itself while being notified.
    [...listeners].forEach((listener) => listener());
    listeners.clear();
  };

  return {
    hasFallenBack: () => fallenBack,
    fallBack,
    onFallBack: (listener) => {
      if (fallenBack) {
        // Already decided — tell the caller now rather than never.
        listener();
        return () => {};
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    wsClientCallbacks: {
      onOpen: () => {
        consecutiveFailures = 0;
      },
      onError: () => {
        consecutiveFailures += 1;
        if (consecutiveFailures >= failuresBeforeFallback) {
          fallBack(`${consecutiveFailures} consecutive WebSocket connection failures`);
        }
      },
    },
  };
};

/**
 * Run a subscription over `primary`, switching to `fallback` when the health
 * signal declares WebSocket unusable or the primary attempt stays silent past
 * {@link SUBSCRIPTION_FIRST_EVENT_TIMEOUT_MS}. The switch is transparent to the
 * caller: the observer sees one continuous subscription, and (because every
 * subscribe replays a `STATE_SNAPSHOT`) no state is lost across it.
 */
export const subscriptionWithFallbackLink = <TRouter extends AnyRouter>({
  primary,
  fallback,
  health,
  firstEventTimeoutMs = SUBSCRIPTION_FIRST_EVENT_TIMEOUT_MS,
}: {
  primary: TRPCLink<TRouter>;
  fallback: TRPCLink<TRouter>;
  health: WsTransportHealth;
  firstEventTimeoutMs?: number;
}): TRPCLink<TRouter> => {
  return (runtime) => {
    const primaryOperation = primary(runtime);
    const fallbackOperation = fallback(runtime);

    return (operation) =>
      observable((observer) => {
        let subscription: Unsubscribable | null = null;
        let usingFallback = false;
        let done = false;
        // Identifies the live attempt. A link is allowed to emit synchronously
        // from `subscribe()`, so an attempt can be superseded before its own
        // `subscribe()` call has even returned — every handler and every
        // assignment is guarded on still being the current attempt.
        let attempt = 0;
        let firstEventTimer: ReturnType<typeof setTimeout> | undefined;
        let unsubscribeHealth: (() => void) | undefined;

        const clearWatchers = () => {
          clearTimeout(firstEventTimer);
          firstEventTimer = undefined;
          unsubscribeHealth?.();
          unsubscribeHealth = undefined;
        };

        const finish = (emit: () => void) => {
          done = true;
          clearWatchers();
          emit();
        };

        const start = (link: ReturnType<TRPCLink<TRouter>>, isFallback: boolean) => {
          const thisAttempt = ++attempt;
          const isCurrent = () => thisAttempt === attempt && !done;
          const started = link(operation).subscribe({
            next: (value) => {
              if (!isCurrent()) {
                return;
              }
              // Anything arriving means this transport works; stop watching.
              clearTimeout(firstEventTimer);
              firstEventTimer = undefined;
              observer.next(value);
            },
            error: (error) => {
              if (!isCurrent()) {
                return;
              }
              if (!isFallback) {
                // The WS link only errors once it has genuinely given up on
                // this operation, so there is nothing to lose by re-trying on
                // the fallback — and everything to gain when the failure was
                // the transport rather than the procedure.
                switchToFallback();
                return;
              }
              finish(() => observer.error(error));
            },
            complete: () => {
              if (!isCurrent()) {
                return;
              }
              finish(() => observer.complete());
            },
          });

          if (thisAttempt === attempt && !done) {
            subscription = started;
          } else {
            started.unsubscribe();
          }
        };

        const switchToFallback = () => {
          if (usingFallback || done) {
            return;
          }
          usingFallback = true;
          clearWatchers();
          const previous = subscription;
          subscription = null;
          // Supersede the primary BEFORE tearing it down. Real links complete
          // their observer from their own teardown — `wsLink`'s unsubscribe
          // calls the pending request's `complete()` — so unsubscribing first
          // would deliver a `complete` that ends the whole subscription just as
          // the fallback is starting, leaving the caller with no transport at
          // all. Bumping the attempt makes those parting emissions no-ops.
          attempt += 1;
          previous?.unsubscribe();
          start(fallbackOperation, true);
        };

        if (health.hasFallenBack()) {
          usingFallback = true;
          start(fallbackOperation, true);
        } else {
          unsubscribeHealth = health.onFallBack(switchToFallback);
          firstEventTimer = setTimeout(() => {
            health.fallBack(`no subscription event within ${firstEventTimeoutMs}ms`);
            switchToFallback();
          }, firstEventTimeoutMs);
          start(primaryOperation, false);
        }

        return () => {
          done = true;
          attempt += 1;
          clearWatchers();
          const current = subscription;
          subscription = null;
          current?.unsubscribe();
        };
      });
  };
};

/**
 * Route an operation over `ws` while WebSocket is healthy and over `http` once
 * it is known broken — decided when the operation starts, never mid-flight.
 * Used for the `app.ws.*` queries and mutations, which are ordinary procedures
 * that only ever travelled the WebSocket for convenience: on a ws-less page
 * they would otherwise sit in tRPC's outgoing buffer forever (a never-opened
 * connection reports no error to a pending request), so a reader could not
 * send a message, stop a run, or undo one.
 */
export const preferHttpWhenWsBrokenLink = <TRouter extends AnyRouter>({
  ws,
  http,
  health,
}: {
  ws: TRPCLink<TRouter>;
  http: TRPCLink<TRouter>;
  health: WsTransportHealth;
}): TRPCLink<TRouter> => {
  return (runtime) => {
    const wsOperation = ws(runtime);
    const httpOperation = http(runtime);
    return (operation) => (health.hasFallenBack() ? httpOperation(operation) : wsOperation(operation));
  };
};
